import type { ProjectCreationOrigin, ProjectStudyRecord } from "@afar-research/contracts";

import { BRAIN_WORKSPACE_RELATIVE_PATH } from "./brainWorkspace.ts";

/** What the Project Study rules read from a project. */
export interface ProjectStudyState {
  readonly createdFrom?: ProjectCreationOrigin | undefined;
  readonly study?: ProjectStudyRecord | undefined;
}

/**
 * A project created from references awaits its Project Study until the study
 * starts or is skipped. Both are recorded once and never undone, so this is
 * derived from facts rather than a phase anyone sets and clears.
 */
export function projectAwaitsStudy(project: ProjectStudyState): boolean {
  return project.createdFrom === "references" && project.study === undefined;
}

/** What a new-thread control says while its project awaits its Project Study. */
export const PROJECT_STUDY_NEW_THREAD_REASON =
  "Start the study first, or skip it. Until then this project keeps to its ingest thread.";

/**
 * Why a new thread cannot be created in the project yet, or null when it can.
 * While a project awaits its Project Study it keeps to the ingest thread its
 * creation started, so the user finishes ingesting and decides what to study
 * before the project's work spreads across threads.
 */
export function projectStudyThreadRefusal(project: ProjectStudyState): string | null {
  if (!projectAwaitsStudy(project)) return null;
  return "This project awaits its Project Study. Start the study, or skip it, before creating other threads; the ingest thread stays available meanwhile.";
}

/**
 * Whether to ask what to study: the project awaits its Project Study and its
 * ingest thread's latest turn completed. While the project waits that thread is
 * the only one it has. A running, failed, or interrupted ingest leaves the
 * question for after the user retries it.
 */
export function shouldOpenProjectStudyDialog(
  project: ProjectStudyState,
  threads: ReadonlyArray<{ readonly latestTurn: { readonly state: string } | null }>,
): boolean {
  return (
    projectAwaitsStudy(project) &&
    threads.some((thread) => thread.latestTurn?.state === "completed")
  );
}

/** Whether the project still offers starting its study: before one starts, skipped or not. */
export function offersProjectStudy(project: ProjectStudyState): boolean {
  return project.createdFrom === "references" && project.study?.status !== "started";
}

/**
 * The Project Study thread's visible first message: the user's words as they
 * wrote them, then one App-written line sending the agent to the Brain first.
 */
export function formatProjectStudyPrompt(study: string): string {
  return `${study.trim()}\n\nStart from what this project's Brain already holds: read ${BRAIN_WORKSPACE_RELATIVE_PATH}/wiki/index.md and the pages it links before anything else.`;
}
